<template>
  <div class="bento-container activity-resource-page">
    <div class="main-section">
      <div class="bento-grid">
        <!-- 顶部控制栏 -->
        <div class="bento-card ai-assistant-card">
          <div class="ai-header-section">
            <div class="header-controls">
              <!-- 类型切换按钮 -->
              <div class="category-type-buttons">
                <div class="type-btn" :class="{ active: searchActivityType === null }" @click="handleTypeChange(null)">全部</div>
                <div class="type-btn type-1" :class="{ active: searchActivityType === 1 }" @click="handleTypeChange(1)">课前活动</div>
                <div class="type-btn type-2" :class="{ active: searchActivityType === 2 }" @click="handleTypeChange(2)">课中活动</div>
                <div class="type-btn type-3" :class="{ active: searchActivityType === 3 }" @click="handleTypeChange(3)">课后活动</div>
              </div>
              <!-- 搜索框 -->
              <div class="sidebar-search">
                <div class="search-input-wrapper">
                  <el-input
                    v-model="searchKeyword"
                    placeholder="搜索活动名称"
                    size="small"
                    clearable
                    class="sidebar-input"
                    @keydown.enter.native="handleSearch"
                    @clear="handleSearch"
                  >
                    <i slot="prefix" class="el-input__icon el-icon-search"></i>
                  </el-input>
                </div>
                <div class="ai-toggle-btn" @click="handleSearch">
                  <i class="el-icon-search"></i>
                  <span>搜索</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 分类卡片 - 顶部显示 -->
      <div class="bento-grid">
        <div class="bento-card category-card">
          <div class="category-header">
            <span class="category-title-text">
              <i class="el-icon-price-tag"></i>
              活动分类
            </span>
            <div class="category-fold-btn" @click="toggleCategoryFold">
              <i :class="isCategoryFolded ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
              <span>{{ isCategoryFolded ? '展开' : '折叠' }}</span>
            </div>
          </div>
          <div class="category-content" v-show="!isCategoryFolded">
            <!-- 一级分类 -->
            <div class="category-line" v-if="level1Categories.length > 0">
              <span class="line-label">一级分类:</span>
              <div class="line-tags">
                <span class="tag-item" :class="{ active: activeLevel1Id === null }" @click="handleLevel1Change(null)">全部</span>
                <span
                  v-for="category in level1Categories"
                  :key="category.activityClassId"
                  class="tag-item"
                  :class="{ active: activeLevel1Id === category.activityClassId }"
                  @click="handleLevel1Change(category.activityClassId)"
                >
                  {{ category.activityClassName }}
                </span>
              </div>
            </div>
            <!-- 二级分类 -->
            <div class="category-line" v-if="level2Categories.length > 0">
              <span class="line-label">二级分类:</span>
              <div class="line-tags">
                <span class="tag-item" :class="{ active: activeLevel2Id === null }" @click="handleLevel2Change(null)">全部</span>
                <span
                  v-for="category in level2Categories"
                  :key="category.activityClassId"
                  class="tag-item"
                  :class="{ active: activeLevel2Id === category.activityClassId }"
                  @click="handleLevel2Change(category.activityClassId)"
                >
                  {{ category.activityClassName }}
                </span>
              </div>
            </div>
            <div class="category-empty" v-if="level1Categories.length === 0">
              暂无分类数据
            </div>
          </div>
        </div>
      </div>

      <!-- 活动卡片区 -->
      <div class="bento-grid" style="position: relative;">
        <div class="bento-card courses-card">
          <div class="courses-header">
            <span class="courses-title-text">
              <i class="el-icon-date"></i>
              活动资源
            </span>
            <span class="courses-count">
              <i class="el-icon-document"></i>
              共 {{ totalCount }} 个活动
            </span>
          </div>

          <div class="courses-content" v-loading="loading">
            <div class="courses-grid" v-if="activityList.length > 0">
              <div
                class="course-card"
                v-for="activity in activityList"
                :key="activity.activityId"
                :class="'activity-card-type-' + activity.activityType"
                @mouseenter="hoverActivityId = activity.activityId"
                @mouseleave="hoverActivityId = null"
                @click="openDetail(activity)"
              >
                <!-- 顶部色块区 -->
                <div class="card-top-band">
                  <div class="card-top-icon">
                    <i v-if="activity.activityType === 1" class="el-icon-sunrise"></i>
                    <i v-else-if="activity.activityType === 2" class="el-icon-video-play"></i>
                    <i v-else class="el-icon-finished"></i>
                  </div>
                  <div class="card-top-right">
                    <span class="card-type-label">{{ getActivityTypeText(activity.activityType) }}</span>
                    <span class="card-creator">
                      <i class="el-icon-user"></i>
                      {{ activity.createusername || '-' }}
                    </span>
                  </div>
                </div>

                <!-- 卡片主体 -->
                <div class="course-card-body">
                  <h3 class="course-title">{{ activity.activityName || '未命名活动' }}</h3>
                  <div class="course-intro boxSizeHidden">
                    {{ activity.activityIntroduce || '暂无简介' }}
                  </div>
                  <!-- 属性行 -->
                  <div class="card-attrs">
                    <span class="attr-item">
                      <i class="el-icon-time"></i>
                      {{ activity.activityDuration || 0 }} 小时
                    </span>
                    <span class="attr-item" :class="'diff-tag-' + activity.activityDifficulty">
                      <i class="el-icon-s-data"></i>
                      {{ getDifficultyText(activity.activityDifficulty) }}
                    </span>
                  </div>
                </div>

                <!-- 卡片底部 -->
                <div class="course-card-footer">
                  <div class="footer-left">
                    <i class="el-icon-calendar"></i>
                    {{ activity.createtime || '-' }}
                  </div>
                  <div class="footer-right">
                    <el-button size="mini" class="action-btn" @click.stop="openDetail(activity)">
                      <i class="el-icon-view"></i>
                      查看
                    </el-button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-else-if="!loading" class="empty-courses">
              <div class="empty-icon">
                <i class="el-icon-date"></i>
              </div>
              <p class="empty-text">暂无活动数据</p>
              <p class="empty-hint">尝试切换活动类型或清空搜索词</p>
            </div>
          </div>

          <!-- 分页 -->
          <div class="pagination-bar">
            <el-pagination
              background
              layout="prev, pager, next, total"
              :total="totalCount"
              :page-size="pageSize"
              :current-page.sync="currentPage"
              :pager-count="5"
              @current-change="handlePageChange"
            ></el-pagination>
          </div>
        </div>
      </div>
    </div>

    <!-- 详情弹框 -->
    <el-dialog
      :visible.sync="detailDialogVisible"
      :title="currentActivity ? currentActivity.activityName : '活动详情'"
      width="700px"
      class="detail-dialog"
      :close-on-click-modal="false"
    >
      <div class="detail-content" v-loading="detailLoading">
        <!-- 标签区 -->
        <div class="detail-tags-bar">
          <el-tag :type="getTypeTagType(currentActivity ? currentActivity.activityType : 1)" size="small" effect="plain">
            {{ getActivityTypeText(currentActivity ? currentActivity.activityType : 0) }}
          </el-tag>
          <el-tag :type="currentActivity && currentActivity.activityStatus === 2 ? 'success' : 'info'" size="small" effect="plain">
            {{ getStatusText(currentActivity ? currentActivity.activityStatus : 0) }}
          </el-tag>
          <el-tag type="warning" size="small" effect="plain">
            {{ getDifficultyText(currentActivity ? currentActivity.activityDifficulty : 0) }}
          </el-tag>
          <span class="duration-tag">
            <i class="el-icon-time"></i>
            {{ currentActivity ? currentActivity.activityDuration : 0 }} 小时
          </span>
        </div>

        <!-- 基本信息 -->
        <div class="detail-section">
          <div class="detail-section-title"><i class="el-icon-info"></i> 基本信息</div>
          <div class="detail-row">
            <span class="detail-label">创建时间：</span>
            <span class="detail-value">{{ (detailData.createtime || currentActivity && currentActivity.createtime) || '-' }}</span>
          </div>
          <div class="detail-row" v-if="detailData.activityScene || (currentActivity && currentActivity.activityScene)">
            <span class="detail-label">活动场景：</span>
            <span class="detail-value">{{ detailData.activityScene || currentActivity.activityScene }}</span>
          </div>
          <div class="detail-row" v-if="detailData.activityNeedMembersOne || (currentActivity && currentActivity.activityNeedMembersOne)">
            <span class="detail-label">引导师人数：</span>
            <span class="detail-value">{{ detailData.activityNeedMembersOne || currentActivity.activityNeedMembersOne }} 人</span>
          </div>
          <div class="detail-row" v-if="detailData.activityNeedMembersTwo || (currentActivity && currentActivity.activityNeedMembersTwo)">
            <span class="detail-label">助教人数：</span>
            <span class="detail-value">{{ detailData.activityNeedMembersTwo || currentActivity.activityNeedMembersTwo }} 人</span>
          </div>
        </div>

        <!-- 活动简介 -->
        <div class="detail-section">
          <div class="detail-section-title"><i class="el-icon-document"></i> 活动简介</div>
          <div class="detail-text" v-if="detailData.activityIntroduce || (currentActivity && currentActivity.activityIntroduce)">
            {{ detailData.activityIntroduce || currentActivity.activityIntroduce }}
          </div>
          <div class="detail-empty" v-else>暂无简介</div>
        </div>

        <!-- 活动流程 -->
        <div class="detail-section">
          <div class="detail-section-title"><i class="el-icon-s-operation"></i> 活动流程</div>
          <div v-if="getProcessList(detailData.activityProcess || (currentActivity && currentActivity.activityProcess)).length > 0">
            <div class="materials-table">
              <div class="materials-header">
                <span class="col" style="width:13%">主题</span>
                <span class="col" style="width:20%">内容</span>
                <span class="col" style="width:12%">操作时长</span>
                <span class="col" style="width:18%">操作注意事项</span>
                <span class="col" style="width:15%">配套工具</span>
                <span class="col" style="width:12%">备注</span>
              </div>
              <div
                class="materials-row"
                v-for="(p, i) in getProcessList(detailData.activityProcess || (currentActivity && currentActivity.activityProcess))"
                :key="i"
              >
                <span class="col" style="width:13%">{{ p.theme || '-' }}</span>
                <span class="col" style="width:20%">{{ p.content || '-' }}</span>
                <span class="col" style="width:12%">{{ p.operationDuration || '-' }}</span>
                <span class="col" style="width:18%">{{ p.precautions || '-' }}</span>
                <span class="col" style="width:15%">{{ p.tools || '-' }}</span>
                <span class="col" style="width:12%">{{ p.remark || '-' }}</span>
              </div>
            </div>
          </div>
          <div class="detail-empty" v-else>暂无流程</div>
        </div>

        <!-- 所需物料 -->
        <div class="detail-section">
          <div class="detail-section-title"><i class="el-icon-box"></i> 所需物料</div>
          <div v-if="getMaterialsList(detailData.activityNeeds || (currentActivity && currentActivity.activityNeeds)).length > 0">
            <div class="materials-table">
              <div class="materials-header">
                <span class="col" style="width:16%">物料名称</span>
                <span class="col" style="width:10%">数量</span>
                <span class="col" style="width:8%">单位</span>
                <span class="col" style="width:12%">规格</span>
                <span class="col" style="width:14%">采购来源</span>
                <span class="col" style="width:30%">备注</span>
              </div>
              <div
                class="materials-row"
                v-for="(m, i) in getMaterialsList(detailData.activityNeeds || (currentActivity && currentActivity.activityNeeds))"
                :key="i"
              >
                <span class="col" style="width:16%">{{ m.materialName || '未命名' }}</span>
                <span class="col" style="width:10%">{{ m.materialQuantity || 0 }}</span>
                <span class="col" style="width:8%">{{ m.materialUnit || '-' }}</span>
                <span class="col" style="width:12%">{{ m.materialSpecs || '-' }}</span>
                <span class="col" style="width:14%">{{ m.purchaseSource || '-' }}</span>
                <span class="col" style="width:30%">{{ m.materialRemark || '-' }}</span>
              </div>
            </div>
          </div>
          <div class="detail-empty" v-else>暂无物料</div>
        </div>

        <!-- 附件资料 -->
        <div class="detail-section">
          <div class="detail-section-title"><i class="el-icon-folder-opened"></i> 附件资料</div>
          <div v-if="getFilesList(detailData.activityFiles || (currentActivity && currentActivity.activityFiles)).length > 0" class="file-list">
            <div
              v-for="(file, i) in getFilesList(detailData.activityFiles || (currentActivity && currentActivity.activityFiles))"
              :key="i"
              class="file-item"
              @click="previewAttachment(file)"
            >
              <i v-if="getFileType(file) === 'image'" class="el-icon-picture" style="color:#409eff"></i>
              <i v-else-if="getFileType(file) === 'video'" class="el-icon-video-camera" style="color:#e6a23c"></i>
              <i v-else-if="getFileType(file) === 'pdf'" class="el-icon-document" style="color:#f56c6c"></i>
              <i v-else class="el-icon-document" style="color:#909399"></i>
              <div class="file-info">
                <div class="file-name">{{ getFileName(file) }}</div>
                <div class="file-hint">点击预览</div>
              </div>
            </div>
          </div>
          <div class="detail-empty" v-else>暂无附件</div>
        </div>
      </div>
      <div slot="footer">
        <el-button @click="detailDialogVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAjActivityList, getAjActivityDetails, getAjActivityClassList, getAjActivityClassListChild } from "../../api/api";

export default {
  name: "ActivityResource",
  data() {
    return {
      searchKeyword: "",
      searchActivityType: null,
      // 分类相关
      level1Categories: [],
      level2Categories: [],
      activeLevel1Id: null,
      activeLevel2Id: null,
      isCategoryFolded: false,
      activityList: [],
      totalCount: 0,
      currentPage: 1,
      pageSize: 12,
      loading: false,
      detailLoading: false,
      currentActivity: null,
      detailData: {},
      detailDialogVisible: false,
      hoverActivityId: null,
      coursesCardHeight: 0,
    };
  },
  created() {
    this.loadLevel1ByType();
    this.getListData();
  },
  mounted() {
    this.calculateHeight();
    window.addEventListener("resize", this.calculateHeight);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.calculateHeight);
  },
  methods: {
    // 点击一级分类
    handleLevel1Change(id) {
      this.activeLevel1Id = id === this.activeLevel1Id ? null : id;
      this.activeLevel2Id = null;
      this.level2Categories = [];
      if (this.activeLevel1Id) {
        this.loadLevel2ByParentId(this.activeLevel1Id);
      }
      this.currentPage = 1;
      this.getListData();
    },
    // 点击二级分类
    handleLevel2Change(id) {
      this.activeLevel2Id = id === this.activeLevel2Id ? null : id;
      this.currentPage = 1;
      this.getListData();
    },
    // 切换分类折叠状态
    toggleCategoryFold() {
      this.isCategoryFolded = !this.isCategoryFolded;
    },
    calculateHeight() {
      // 顶部控制卡高度约 80px + 间距 20px + margin
      const total = window.innerHeight;
      this.coursesCardHeight = total - 80 - 20 - 30 - 15 - 15 - 80;
    },
    handleTypeChange(type) {
      this.searchActivityType = type;
      this.activeLevel1Id = null;
      this.activeLevel2Id = null;
      this.level2Categories = [];
      if (type) {
        this.loadLevel1ByType();
      }
      this.currentPage = 1;
      this.getListData();
    },
    // 加载一级分类
    async loadLevel1ByType() {
      const res = await getAjActivityClassList({ activityClassParentId: 0 });
      if (res.data) {
        this.level1Categories = res.data;
      }
    },
    // 加载一级分类的二级分类
    async loadLevel2ByParentId(parentId) {
      const res = await getAjActivityClassList({ activityClassParentId: parentId });
      if (res.data) {
        this.level2Categories = res.data;
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.getListData();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.getListData();
    },
    getActivityTypeText(type) {
      return { 1: "课前活动", 2: "课中活动", 3: "课后活动" }[type] || "未知";
    },
    getStatusText(status) {
      return { 1: "草稿", 2: "已发布" }[status] || "未知";
    },
    getDifficultyText(d) {
      return { 1: "简单", 2: "中等", 3: "困难" }[d] || "-";
    },
    getTypeTagType(type) {
      return { 1: "primary", 2: "success", 3: "warning" }[type] || "";
    },
    getMaterialsList(needs) {
      if (!needs) return [];
      try { return typeof needs === "string" ? JSON.parse(needs) : needs; } catch { return []; }
    },
    // 获取活动流程列表
    getProcessList(process) {
      if (!process) return [];
      try {
        if (typeof process === 'string') {
          return JSON.parse(process);
        }
        return process;
      } catch {
        return [];
      }
    },
    getFilesList(files) {
      if (!files) return [];
      try { return typeof files === "string" ? JSON.parse(files) : files; } catch { return []; }
    },
    getFileName(url) {
      if (!url) return "附件";
      try { url = decodeURIComponent(url); } catch (e) {}
      return (url.split("/").pop() || "附件").split("?")[0] || "附件";
    },
    getFileType(url) {
      if (!url) return "other";
      const ext = this.getFileName(url).toLowerCase().split(".").pop() || "";
      if (["jpg","jpeg","png","gif","bmp","webp","svg"].includes(ext)) return "image";
      if (["mp4","avi","mov","flv","wmv","mkv","webm"].includes(ext)) return "video";
      if (ext === "pdf") return "pdf";
      if (["doc","docx"].includes(ext)) return "word";
      if (["xls","xlsx"].includes(ext)) return "excel";
      if (["ppt","pptx"].includes(ext)) return "ppt";
      return "other";
    },
    previewAttachment(url) {
      if (!url) return;
      const type = this.getFileType(url);
      const fileName = this.getFileName(url);
      if (type === "video") { window.open(url, "_blank"); return; }
      if (type === "pdf") {
        const w = window.open("", "_blank");
        w.document.write(`<html><head><title>${fileName}</title><style>*{margin:0;padding:0}iframe{width:100vw;height:100vh;border:none}</style></head><body><iframe src="${url}"></iframe></body></html>`);
        return;
      }
      if (["word","excel","ppt"].includes(type)) {
        window.open(`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`, "_blank");
        return;
      }
      window.open(url, "_blank");
    },
    async getListData() {
      this.loading = true;
      try {
        const params = { currentPage: this.currentPage, pageSize: this.pageSize, activityName: this.searchKeyword };
        if (this.searchActivityType) params.activityType = this.searchActivityType;
        // 添加分类筛选 - 选中二级分类时筛选二级，选中一级分类时筛选一级
        if (this.activeLevel2Id) {
          params.activityClassId = this.activeLevel2Id;
        } else if (this.activeLevel1Id) {
          params.activityClassId = this.activeLevel1Id;
        }
        const res = await getAjActivityList(params);
        if (res.status === 200) {
          this.activityList = res.data || [];
          this.totalCount = res.total || 0;
        } else {
          this.$message.error(res.msg || "获取活动列表失败");
        }
      } catch (e) {
        console.error("获取活动列表失败:", e);
      } finally {
        this.loading = false;
      }
    },
    openDetail(activity) {
      this.currentActivity = activity;
      this.detailData = {};
      this.detailDialogVisible = true;
      this.getActivityDetail(activity.activityId);
    },
    async getActivityDetail(activityId) {
      this.detailLoading = true;
      try {
        const res = await getAjActivityDetails({ activityId });
        if (res.status === 200) { this.detailData = res.data || {}; }
        else { this.$message.error(res.msg || "获取详情失败"); }
      } catch (e) {
        console.error("获取活动详情失败:", e);
      } finally {
        this.detailLoading = false;
      }
    },
  },
};
</script>

<style scoped>
/* ===== 整体容器，与课程资源页保持一致 ===== */
.activity-resource-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 15px 20px;
  gap: 16px;
  background: linear-gradient(135deg, #f0f4ff 0%, #e8eeff 100%);
  animation: fadeIn 0.5s ease-in-out;
  overflow-y: auto;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes shimmer {
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.main-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow: hidden;
  min-width: 0;
}

.bento-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}

/* ===== 通用 bento-card 风格 ===== */
.bento-card {
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  border-radius: 20px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(139, 92, 246, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(139, 92, 246, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
}

.bento-card::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, #8b5cf6, #6d28d9, #8b5cf6);
  background-size: 200% 100%;
  opacity: 0;
  transition: opacity 0.3s;
}

.bento-card:hover::before {
  opacity: 1;
  animation: shimmer 2s infinite;
}

.bento-card:hover {
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08);
}

/* ===== 顶部控制栏 ===== */
.ai-assistant-card { padding: 16px 20px; }

.ai-header-section { display: flex; flex-direction: column; gap: 12px; }

.header-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 16px;
}

.category-type-buttons {
  display: flex;
  gap: 10px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.type-btn {
  padding: 8px 20px;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.type-btn:hover {
  border-color: #6366f1;
  color: #6366f1;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
}

/* 全部 - 紫色 */
.type-btn.active {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border-color: #6366f1;
  color: white;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.35);
}

/* 课前活动 - 蓝色 */
.type-btn.type-1.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-color: #3b82f6;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.35);
}

/* 课中活动 - 绿色 */
.type-btn.type-2.active {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: #10b981;
  box-shadow: 0 4px 16px rgba(16, 185, 129, 0.35);
}

/* 课后活动 - 橙色 */
.type-btn.type-3.active {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: #f59e0b;
  box-shadow: 0 4px 16px rgba(245, 158, 11, 0.35);
}

/* 搜索 */
.sidebar-search {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-input-wrapper { width: 200px; }

.sidebar-input ::v-deep .el-input__inner {
  border-radius: 10px;
  border: 2px solid #e5e7eb;
  background: #f8fafc;
  font-size: 13px;
  transition: all 0.3s;
}

.sidebar-input ::v-deep .el-input__inner:focus {
  border-color: #8b5cf6;
  background: white;
  box-shadow: 0 0 0 4px rgba(139, 92, 246, 0.12);
}

.ai-toggle-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  color: white;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 14px rgba(139, 92, 246, 0.35);
  white-space: nowrap;
}

.ai-toggle-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(139, 92, 246, 0.45);
}

/* ===== 课程卡片容器 ===== */
.courses-card {
  display: flex;
  flex-direction: column;
  padding: 0;
  min-height: 400px;
  max-height: calc(100vh - 280px);
  overflow: hidden;
}

.courses-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  flex-shrink: 0;
}

.courses-title-text {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.courses-title-text i { color: #8b5cf6; }

.courses-count {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #94a3b8;
}

/* 卡片内容滚动区 */
.courses-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.courses-content::-webkit-scrollbar { width: 6px; }
.courses-content::-webkit-scrollbar-track { background: transparent; }
.courses-content::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 3px; }
.courses-content::-webkit-scrollbar-thumb:hover { background: #9ca3af; }

/* ===== 分类侧边栏 ===== */
.category-card {
  width: 100%;
  margin-bottom: 10px;
  transition: all 0.3s ease;
  padding: 10px 16px;
}

.category-card.folded {
  width: 60px;
}

.category-card.folded .category-header {
  justify-content: center;
  padding: 12px 8px;
}

.category-card.folded .category-title-text {
  display: none;
}

.category-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 12px;
}

.category-title-text {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 6px;
}

.category-title-text i {
  color: #f59e0b;
}

.category-fold-btn {
  cursor: pointer;
  color: #94a3b8;
  font-size: 14px;
  padding: 4px;
  transition: color 0.2s;
}

.category-fold-btn:hover {
  color: #f59e0b;
}

.category-content {
  max-height: 80px;
  overflow-y: auto;
}

.category-content::-webkit-scrollbar {
  width: 4px;
}

.category-content::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 2px;
}

/* 分类行样式 */
.category-line {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.category-line:last-child {
  margin-bottom: 0;
}

.line-label {
  font-size: 12px;
  color: #94a3b8;
  width: 70px;
  flex-shrink: 0;
}

.line-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tag-item {
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}

.tag-item:hover {
  background: #e2e8f0;
  color: #334155;
}

.tag-item.active {
  background: #eff6ff;
  color: #3b82f6;
  border-color: #3b82f6;
}

.category-empty {
  text-align: center;
  color: #94a3b8;
  font-size: 13px;
  padding: 20px 0;
}

/* ===== 卡片网格 ===== */
.courses-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}

/* ===== 活动卡片 ===== */
.course-card {
  background: white;
  border-radius: 14px;
  border: 1px solid #f1f5f9;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.course-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(139, 92, 246, 0.15);
  border-color: #c4b5fd;
}

/* ===== 顶部色块区 ===== */
.card-top-band {
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}

.card-top-band::after {
  content: '';
  position: absolute;
  right: -20px;
  top: -20px;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.12);
}

/* 课前 - 蓝 */
.activity-card-type-1 .card-top-band {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}
/* 课中 - 绿 */
.activity-card-type-2 .card-top-band {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}
/* 课后 - 橙 */
.activity-card-type-3 .card-top-band {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.card-top-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 22px;
  flex-shrink: 0;
  transition: transform 0.3s;
}

.course-card:hover .card-top-icon {
  transform: scale(1.08) rotate(-4deg);
}

.card-top-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
}

.card-type-label {
  font-size: 12px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.95);
  letter-spacing: 0.5px;
}

.card-status-dot {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.4);
}

.card-status-dot.status-dot-2 {
  background: rgba(255, 255, 255, 0.35);
}

/* 创建人样式 */
.card-creator {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.25);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  max-width: 80px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-creator i {
  font-size: 10px;
}

/* 标题 */
.course-title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 属性行 */
.card-attrs {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  flex-wrap: wrap;
}

.attr-item {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 3px 9px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.attr-item i { font-size: 11px; }

.diff-tag-1 { color: #059669 !important; }
.diff-tag-2 { color: #d97706 !important; }
.diff-tag-3 { color: #dc2626 !important; }

.status-tag.status-2 { color: #059669 !important; }
.status-tag.status-1 { color: #94a3b8 !important; }

/* 卡片主体 */
.course-card-body {
  padding: 14px 16px 10px;
  flex: 1;
}

.course-intro {
  font-size: 12.5px;
  color: #94a3b8;
  line-height: 1.65;
}

.boxSizeHidden {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 卡片底部 */
.course-card-footer {
  padding: 10px 16px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fafbfc;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: #94a3b8;
}

.action-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%) !important;
  border: none !important;
  color: white !important;
  border-radius: 6px !important;
  font-size: 12px !important;
}

.action-btn:hover {
  background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%) !important;
  transform: translateY(-1px);
}

/* ===== 空状态 ===== */
.empty-courses {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #94a3b8;
}

.empty-courses .empty-icon {
  width: 72px;
  height: 72px;
  background: #f1f5f9;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.empty-courses .empty-icon i { font-size: 32px; color: #cbd5e1; }
.empty-courses .empty-text { margin: 0 0 6px; font-size: 15px; font-weight: 600; color: #475569; }
.empty-courses .empty-hint { margin: 0; font-size: 13px; color: #94a3b8; }

/* ===== 分页 ===== */
.pagination-bar {
  padding: 12px 20px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: center;
  background: white;
  flex-shrink: 0;
}

.pagination-bar ::v-deep .el-pagination.is-background .el-pager li {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.pagination-bar ::v-deep .el-pagination.is-background .el-pager li:not(.disabled).active {
  background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);
  border-color: transparent;
}

/* ===== 详情弹框 ===== */
.detail-dialog ::v-deep .el-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.detail-dialog ::v-deep .el-dialog__header {
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
  font-weight: 700;
}

.detail-dialog ::v-deep .el-dialog__body { padding: 0; }

.detail-content {
  padding: 20px 24px;
  max-height: 520px;
  overflow-y: auto;
}

.detail-content::-webkit-scrollbar { width: 4px; }
.detail-content::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }

.detail-tags-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

.duration-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 4px 10px;
  border-radius: 4px;
}

.detail-section { margin-bottom: 20px; }
.detail-section:last-child { margin-bottom: 0; }

.detail-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f1f5f9;
}

.detail-section-title i { color: #8b5cf6; }

.detail-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 13px;
}

.detail-label { width: 80px; flex-shrink: 0; color: #64748b; }
.detail-value { color: #1e293b; }

.detail-text {
  font-size: 13px;
  color: #475569;
  line-height: 1.8;
  background: #f8fafc;
  padding: 12px 14px;
  border-radius: 8px;
  white-space: pre-wrap;
}

.detail-empty { color: #94a3b8; font-size: 13px; font-style: italic; }

/* 物料表格 */
.materials-table {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
}

.materials-header {
  display: flex;
  background: #f3f4f6;
  padding: 10px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.materials-row {
  display: flex;
  padding: 10px 12px;
  font-size: 13px;
  color: #1f2937;
  border-top: 1px solid #f3f4f6;
}

.materials-row:nth-child(even) { background: #f9fafb; }

.col {
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 附件 */
.file-list { display: flex; flex-wrap: wrap; gap: 10px; }

.file-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 160px;
}

.file-item:hover {
  background: #f0edff;
  border-color: #8b5cf6;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(139, 92, 246, 0.12);
}

.file-item i { font-size: 20px; flex-shrink: 0; }

.file-info { flex: 1; min-width: 0; }

.file-name {
  font-size: 12px;
  color: #303133;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
  font-weight: 500;
}

.file-hint { font-size: 11px; color: #909399; margin-top: 2px; }
.file-item:hover .file-hint { color: #8b5cf6; }

/* ===== 响应式 ===== */
@media (max-width: 1400px) {
  .courses-grid { grid-template-columns: repeat(3, 1fr); }
}

@media (max-width: 1100px) {
  .courses-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
